import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnInit,
  output,
  signal
} from '@angular/core';
import {
  ApplePayService,
  ApplePayPaymentRequest,
  ApplePayTokenResult
} from '../../../core/services/apple-pay/apple-pay.service';

@Component({
  selector: 'app-apple-pay-button',
  standalone: true,
  templateUrl: './apple-pay-button.component.html',
  styleUrl: './apple-pay-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApplePayButtonComponent implements OnInit {
  /** Total amount to charge (as string, e.g. "12.50") */
  amount = input<string>('0.00');

  /** Display label shown on the Apple Pay sheet (e.g. "Happy Ordering") */
  label = input<string>('Happy Ordering');

  /** ISO 4217 currency code (e.g. "USD") */
  currencyCode = input<string>('USD');

  /** ISO 3166-1 alpha-2 country code (e.g. "US") */
  countryCode = input<string>('US');

  /** Whether the button should be disabled */
  disabled = input<boolean>(false);

  /** Emitted when Apple Pay returns an encrypted payment token */
  tokenReceived = output<ApplePayTokenResult>();

  /** Emitted when the Apple Pay session fails or the user cancels */
  paymentError = output<string>();

  /** Whether Apple Pay is available on this device/browser */
  readonly isAvailable = signal(false);

  /** Whether a payment session is currently in progress */
  readonly isProcessing = signal(false);

  /** Any error message to display */
  readonly error = signal<string | null>(null);

  constructor(private applePayService: ApplePayService) {}

  ngOnInit(): void {
    this.isAvailable.set(this.applePayService.isAvailable());
  }

  async onApplePayClick(): Promise<void> {
    if (this.disabled() || this.isProcessing() || !this.isAvailable()) {
      return;
    }

    this.isProcessing.set(true);
    this.error.set(null);

    const paymentRequest: ApplePayPaymentRequest = {
      countryCode: this.countryCode(),
      currencyCode: this.currencyCode(),
      total: {
        label: this.label(),
        amount: this.amount(),
        type: 'final'
      }
    };

    try {
      const result = await this.applePayService.startSession(paymentRequest);
      this.tokenReceived.emit(result);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Apple Pay payment failed.';

      // Don't show error for user cancellation
      if (!message.includes('cancelled')) {
        this.error.set(message);
        this.paymentError.emit(message);
      }
    } finally {
      this.isProcessing.set(false);
    }
  }
}
